/**
 * PR-G (DD-6 / E5) — the demo showcase's shared clock anchor.
 *
 * The `?user=ramiro` fixtures used to hard-code dates (season anchored
 * Apr 6 2026, race "Tue, May 20"). Real time kept moving, so the hero's
 * macro-derived week drifted ("Week 7") away from the frozen "This week"
 * grid (Apr 20–26) and the race fell into the past with a wrong weekday.
 *
 * Fix: every demo fixture derives from ONE anchor — the **Monday of the
 * real current week**, which the showcase treats as "today" (Monday is
 * the Easy-run day, so the hero's "Easy run, 3 miles." always matches
 * the grid's today-marker). The season is anchored so that week is
 * Week 2 · Base (matching `ramiroToday.phaseLabel`), and the race sits
 * 30 days out (matching the "30 days" motivational line). Nothing can
 * drift again, by construction.
 *
 * Pure helpers take an injected `now` so tests pin the clock (Q1 flake
 * discipline); the fixture modules call them once at load with the real
 * clock.
 */

import { todayMondayBasedLocal } from '../lib/weekdayUtils';

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

/** Monday of the real current week, 00:00 local — the demo "today". */
export function demoAnchorMonday(now: Date = new Date()): Date {
  const monday = new Date(now);
  monday.setHours(0, 0, 0, 0);
  monday.setDate(now.getDate() - todayMondayBasedLocal(now));
  return monday;
}

export function addDays(d: Date, days: number): Date {
  const out = new Date(d);
  out.setDate(d.getDate() + days);
  return out;
}

/** Local-timezone YYYY-MM-DD (the `week_start_date` column format). */
export function toLocalIso(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/** "Jun 8" */
export function fmtMonthDay(d: Date): string {
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

/** "Mon, Jun 8" */
export function fmtDowMonthDay(d: Date): string {
  return `${DOW[d.getDay()]}, ${fmtMonthDay(d)}`;
}

/** "Jun 8 to 14" (same month) · "Jun 29 to Jul 5" (crossing one) — the fixture
 *  week-range style, on the ONE rule the live surfaces use (B87 leftovers,
 *  2026-09-08; the month collapsed by UX round 4 D-A, 2026-09-09): the demo
 *  Today card sits beside the real SectionLabel and must not read
 *  differently. */
export function fmtWeekRange(monday: Date): string {
  const sunday = addDays(monday, 6);
  const end =
    monday.getMonth() === sunday.getMonth() ? String(sunday.getDate()) : fmtMonthDay(sunday);
  return `${fmtMonthDay(monday)} to ${end}`;
}
